<template>
    <div class="result-hero-details" :class="{ full: declinedAndAlt.includes(status) }">
        <div class="details">
            <div class="title">
                <span v-if="applicationData.first_name" class="d-flex greeting">
                    {{ $t('results.heroBanner.greeting') }} {{ capitalize(applicationData.first_name) }},
                </span>

                <span v-else class="greeting"> {{ $t('results.heroBanner.greeting') }}, </span>

                <span class="greetingDetail" v-if="isLendindtree && !hasNonAlternativeOffers">
                    {{ $t('results.heroBanner.lendindtreeAlternativeGreetingDetail') }}
                </span>

                <span class="greetingDetail" v-else-if="isDebtSolutionOffer">
                    {{ $t('results.heroBanner.debtSolutionGreetingDetail') }}

                    <span :class="[shouldHighlightOffer ? 'offer-highlight' : '']">
                        {{ $t('results.heroBanner.debtSolutionOffer') }}.
                    </span>
                </span>

                <span class="greetingDetail" v-else-if="status == 'current'">
                    {{ $t('results.heroBanner.currentGreetingDetail') }}

                    <span :class="[shouldHighlightOffer ? 'offer-highlight' : '']">
                        {{ numberOfOffersAvailable }} {{ $tc('results.heroBanner.offer', numberOfOffersAvailable) }}.
                    </span>
                </span>

                <span class="greetingDetail" v-else-if="status == 'expired'">
                    {{ $t('results.heroBanner.expiredGreetingDetail', { numberOfOffers: numberOfOffersAvailable }) }}
                    <span :class="[shouldHighlightOffer ? 'offer-highlight' : '']">
                        {{ $t('results.heroBanner.expired') }}.
                    </span>
                </span>

                <span class="greetingDetail" v-else-if="declinedAndAlt.includes(status)">
                    {{ $t('results.heroBanner.declinedGreetingDetail', { numberOfOffers: numberOfOffersAvailable }) }}.
                </span>
            </div>

            <div v-if="hasLoanDataAndIsPersonalLoan" class="details-description">
                <span
                    v-html="
                        $t('results.heroBanner.detailsDescription', {
                            displayLoanAmount: displayLoanAmount,
                            loanTerm: displayLoanTerm,
                            loanPurposeDisplay: applicationData.loan_purpose_display.toLowerCase(),
                        })
                    "
                ></span>
                &nbsp
                <span v-if="declinedAndAlt.includes(status)">{{ $t('results.heroBanner.declinedCTA') }}</span>
            </div>

            <div class="details-description" v-else-if="isDebtSolutionOffer">
                <span v-text="$t('results.heroBanner.debtSolutionDetailsDescription')"></span>
            </div>

            <div class="details-description">
                <span v-if="['current'].includes(status) && numberOfExpiredOffers">
                    {{
                        $t('results.heroBanner.expiredCTA', {
                            numberOfExpiredOffers: numberOfExpiredOffers,
                            s: numberOfExpiredOffers > 1 ? 's' : '',
                        })
                    }}
                </span>
            </div>

            <div class="detail-actions">
                <button
                    class="button search-button"
                    v-if="status != 'expired' && resubmitEnabled && !isCreditCardCampaign"
                    @click="emitEvent('resubmitModal')"
                >
                    {{ $t('results.heroBanner.buttons.updateSearch') }}
                </button>

                <button
                    class="button search-button"
                    v-else-if="
                        status === 'expired' &&
                        isCreditCardCampaign &&
                        (resubmitEnabled || showResubmitFormOnExpiredResults)
                    "
                    @click="emitEvent('refreshOffer', { result: currentInstance })"
                >
                    {{ $t('results.heroBanner.buttons.updateSearch') }}
                </button>

                <button
                    class="button search-button"
                    v-else-if="status === 'expired' && (resubmitEnabled || showResubmitFormOnExpiredResults)"
                    @click="emitEvent('refreshOffer', { result: currentInstance })"
                >
                    {{ $t('results.heroBanner.buttons.refreshResults') }}
                </button>

                <button
                    class="button bordered hollow icon-button"
                    v-if="previousResultReference !== null && previousSearchEnabled"
                    @click="previousResult"
                >
                    <i class="far fa-undo-alt"></i>
                    {{ $t('results.heroBanner.buttons.previousHistory') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { formatCurrency } from '@js/utilities/number.js'

// lodash components
import take from 'lodash/take'
import capitalize from 'lodash/capitalize'
import toLower from 'lodash/toLower'
import some from 'lodash/some'
import split from 'lodash/split'
import sumBy from 'lodash/sumBy'

export default {
    setup() {
        const store = useStore()
        const route = useRoute()
        const router = useRouter()

        return {
            route,
            router,
            store,
        }
    },
    props: {
        declinedLenders: Array,
        numberOfOffers: Number,
        numberOfExpiredOffers: Number,
        applicationData: Object,
        isDebtSolutionOffer: Boolean,
        offers: Object,
        hasNonAlternativeOffers: Object,
    },
    data() {
        return {
            declinedAndAlt: ['declined', 'alternative', 'flat-declined'],
            yearWithMonth: null,
            iframeOffsetTop: 0,
        }
    },
    mounted() {
        this.iframeAllowedSites = JSON.parse(this.themeSetting('iframe_allowed_sites'))

        window.addEventListener(
            'message',
            event => {
                const messageContent = event.data

                if (some(this.iframeAllowedSites, ['name', event.origin])) {
                    const variables = split(messageContent, '|')
                    this.iframeOffsetTop = variables[5]
                }
            },
            false,
        )
    },
    computed: {
        resubmitEnabled: function () {
            return this.store.state.global.data.resubmit_show_form === '1' ? true : false
        },
        showResubmitFormOnExpiredResults: function () {
            return this.store.state.global.data.show_resubmit_form_on_expired_result === '1'
        },
        shouldHighlightOffer() {
            return parseInt(this.themeSetting('hide_show_hero_offer_highlight')) !== 1
        },
        status() {
            return this.$store.state.result.status
        },
        currentInstance: function () {
            return this
        },
        displayLoanAmount: function () {
            return formatCurrency(this.applicationData.loan_amount)
        },
        firstLenders: function () {
            const show = window.innerWidth >= 992 ? 3 : 2
            return take(this.declinedLenders, show)
        },
        displayLoanTerm: function () {
            const months = this.applicationData.loan_term
            const years = this.formatMonths(months)
            let scale
            let scaleYear
            let scaleMonth

            if (months <= 11) {
                scale =
                    parseInt(months) === 1 ? this.$t('results.filtering.month') : this.$t('results.filtering.months')
            } else if (this.yearWithMonth && parseInt(years)) {
                scaleYear =
                    parseInt(years) === 1 ? this.$t('results.filtering.year') : this.$t('results.filtering.years')
                scaleMonth =
                    parseInt(this.yearWithMonth) === 1
                        ? this.$t('results.filtering.month')
                        : this.$t('results.filtering.months')

                return `${years} ${toLower(scaleYear)} and ${this.yearWithMonth} ${toLower(scaleMonth)}`
            } else {
                scale = parseInt(years) === 1 ? this.$t('results.filtering.year') : this.$t('results.filtering.years')
            }

            const amount = months <= 11 ? months : years

            return `${amount} ${toLower(scale)}`
        },
        previousResultReference() {
            return this.store.getters.getPreviousResult(this.currentResultReference)
        },
        currentResultReference() {
            const currentPath = this.route.fullPath
            const regex = /result\/([a-f0-9]{32})$/
            const matches = regex.exec(currentPath)
            return matches ? matches[1] : null
        },
        isCreditCardCampaign: function () {
            return window.CAMPAIGN_TYPE === 'Credit Card'
        },
        hasLoanDataAndIsPersonalLoan: function () {
            return (
                this.applicationData.loan_term &&
                this.applicationData.loan_amount &&
                this.applicationData.loan_purpose_display &&
                !this.isCreditCardCampaign &&
                !this.isDebtSolutionOffer
            )
        },
        numberOfOffersAvailable: function () {
            if (this.isCreditCardCampaign) {
                return sumBy(this.offers, offer =>
                    offer.type !== 'alternative' && offer.type !== 'debt_solution' ? 1 : 0,
                )
            }

            return this.numberOfOffers
        },
        isLendindtree: function () {
            return process.env.IS_LENDINDTREE
        },
        previousSearchEnabled: function () {
            return this.store.state.global.data.previous_search === '1'
        },
    },
    methods: {
        capitalize: capitalize,
        formatMonths: function (value) {
            let years = 0
            years = parseInt(value / 12)
            this.yearWithMonth = value % 12
            return years
        },
        previousResult: function () {
            this.previousHistory()
            this.emitEvent('previousHistory', this.previousResultReference)
        },
        emitEvent: function (eventName, payload) {
            this.emitter.emit(eventName, payload)
        },
        previousHistory: function () {
            this.trackEvent('event', {
                eventCategory: 'button',
                eventAction: 'click',
                eventLabel: 'previous-search-history',
            })
        },
    },
}
</script>
